// MSB(마스터 스토리보드) V2 레이아웃 컴포넌트 10종 (§3-11) — 스펙 재구현.
// 결정론 조립의 핵심: MSB SceneSpec.layout { type, content, revealMode, anchor, keyword } 를
// 그대로 받아 렌더한다 (LLM 코드 생성 없이 스키마→컴포넌트 매핑).
//
// 공통 계약 (§3-11):
//   - colors: AtmosphereColors (페르소나 톤온톤) / f: 현재 프레임
//   - revealMode: 'all' | 'sequential'(25f 간격) | 'cascade'(18→15→12→8 가속)
//   - anchor: 9방향 (기본 center · top/bottom = 상단중앙/하단중앙) / keyword: 자동 강조 (KeywordText)
//   - 자체 entry stagger + 양방향 clamp 내장 (§8-0 하드룰 — 훅에 baked-in)
//   - 카드 height 고정 금지(padding·minHeight만) · 그룹 alignItems:stretch · 한 행 카드 16:9≤3 (§8-0)
//   - 텍스트: KO_TEXT 스프레드 · LINE_HEIGHT/FW/T 토큰 lookup (직접 숫자 금지)
//   - 안전 영역: 세이프존 정본(`@/lib/safe-zone` core) × 실제 해상도 (useVideoConfig — 해상도 독립 · 좌우 비대칭)
import React from 'react';
import { AbsoluteFill, useVideoConfig } from 'remotion';
// 🛡 씬 이미지 크래시 내성 래퍼(PR#983): 프리뷰/썸네일=순수 <img>, 실제 렌더만 Remotion <Img> 게이팅.
import { SceneImg } from '../templates/SceneImg';
import {
  CARD,
  DIVIDER,
  FW,
  KO_TEXT,
  LAYOUT,
  LINE_HEIGHT,
  T,
  type AtmosphereColors,
} from './tokens';
import { KeywordText, slideIn, type KeywordEmphasisId , sustainedPushIn, sustainedFloat, motionEnterFrames, propPop, type MotionKitOpt,
  KitWords, KitCount, KitPop, KitCursor, wordsDoneAt, stepProgress, kitFrames, GlueText } from './motion';
// 📐 세이프존 SSOT(데드존 가이드 실측) — 장표 전 종류의 안전 영역이 여기서 나온다(`useSafeArea`).
import { safeInsetPx } from '../../lib/safe-zone';
import { SlideStageInsetContext, useStageCells, useStageCompact, useStageHidesTitle, useStageMarks, useStageSubCols } from './stage-inset';
import { BRIEF_COMPACT, BRIEF_COMPACT_CELL, BRIEF_SUB_COL_GAP, briefFullTextSubCols } from './motion-brief-shape';
// 🧰 글자 노출 하한 max(1.5s, 글자수/6) — 부품 상자 규율(`motionKit` 일 때만 항목 등장 마감에 건다).
import { minHoldSec } from '../../lib/motion-kit/pick';
// 🧹 #6120 — 표식 이중(✕❌)·숫자+단위 줄 갈림·수치 값 넘침을 «그릴 때»만 바로잡는다(저장 데이터 그대로).
import { planStatValue, splitNumberRuns, stripLeadingMark } from './slide-text-fit';
// 📐 #6119 2차 — 자막 위 칸에 안 들어가는 장표는 장표 층이 «자리 짜기» 단계를 올린다(0 = 종전).
import { useSlideRoom } from './slide-caption-fit';
import { useBalancedTitle } from './title-balance';

/** 숫자+단위 덩이를 줄바꿈 없는 따로 상자로 그린다(«13만 / 2천 원» 방지) — 덩이가 없으면 글 그대로(종전 HTML). 붙임 문자 대신 상자인 이유는 `slide-text-fit` 머리말. */
export const NumRunText: React.FC<{ text: string }> = ({ text }) => {
  const pieces = splitNumberRuns(text);
  // 덩이 밖 조각도 엔진이 여러 조각으로 재는 어절(«할\u00A0수»·«(10년)»)은 어절 상자로(#6306 `GlueText`).
  if (!pieces.some((p) => p.run)) return <GlueText text={text} />;
  return <>{pieces.map((p, i) => (p.run ? <span key={i} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>{p.text}</span> : <GlueText key={i} text={p.text} />))}</>;
};

// ─── 공통 타입·헬퍼 ─────────────────────────────────────────────

export type RevealMode = 'all' | 'sequential' | 'cascade';
/** 앵커 9방향. ★'top'·'bottom'(상단중앙·하단중앙)은 채팅 장표 편집(#169)에서 신설★ —
 *  아래 `_anchorFlex` 는 startsWith/endsWith 판정이라 값만 늘리면 배치 코드는 그대로 동작한다
 *  (top → justifyContent:flex-start + alignItems:center = 상단중앙).
 *  ★같은 목록을 세 곳이 본다★: 이 유니온 · `composition.tsx` 의 런타임 화이트리스트 ·
 *  `lib/templates/slide-edit-contract.SLIDE_ANCHORS`(편집 계약 SSOT). 셋이 어긋나면 채팅으로 지정한
 *  값이 어느 한 경로에서만 조용히 사라지므로, `tests/unit/blueprint-slide-chat-contract.test.ts`
 *  ("렌더러 Anchor 계약")가 세 목록의 ★정확한 일치★를 단정한다. */
export type Anchor = 'center' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left' | 'right';
export type MsbAspect = keyof typeof LAYOUT; // '16:9' | '9:16' | '4:5'

export interface LayoutBaseProps {
  colors: AtmosphereColors;
  f: number;
  revealMode?: RevealMode;
  anchor?: Anchor;
  keyword?: string;
  keywordEmphasis?: KeywordEmphasisId;
  /** 미지정 시 useVideoConfig 해상도에서 자동 판별 */
  aspect?: MsbAspect;
  /** 씬별 카드 스타일 (components.styles.cardStyle 배선) — 카드형 레이아웃만 소비, 미지정 시 각 레이아웃 기본값. */
  cardStyle?: Parameters<typeof CARD>[0];
  /** 🕐 이 씬의 나레이션 문장 시간표(씬 상대 프레임). 목록형 장표가 항목 등장을 여기에 맞춘다.
   *  ★렌더 경계에서만 온다★ — `slide.content` 에 싣지 않는다(fullText 와의 데이터 계약이 갈려
   *  W2 동치 클래스 `slide-auto.canonSlideType` 의 근거가 흔들린다 · kineticTypo `beats` 와 같은 이유).
   *  `kinetic-typo.KineticBeat[]` 가 구조 호환이라 그대로 들어온다. */
  beats?: ReadonlyArray<SlideBeat>;
  /** 🕐 이 씬의 길이(프레임). 시간표가 없을 때 항목 등장을 펴는 기준. 미지정이면 종전 고정 간격. */
  sceneFrames?: number;
  /** ⏱🔤 항목마다 «그 항목을 말하는» 씬 상대 프레임(못 찾은 항목 null · 회장님 2026-10-04 ①안 · `word-cues.slideWordCuesFrom`).
   *  `beats` 와 같은 이유로 ★렌더 경계에서만 온다★(content 에 싣지 않는다). 없으면 종전 시간표 그대로. */
  itemCues?: ReadonlyArray<number | null>;
  /** 🧰 «모션 부품» 스타일(2026-09-30 · 표면 2 · 정본 지도 §12) — true 면 움직임이 부품 상자 규율(이징 토큰·
   *  길이 500ms·소품 스프링·노출 하한)을 따른다. ★렌더 경계에서만 온다★(`Overlay169Template` 이 컨텍스트에서
   *  읽어 준다 · `slide.content` 에 싣지 않는다 — 플래그는 테넌트의 «지금» 값이지 작업물 데이터가 아니다).
   *  미지정/false = 종전 재생 화면 그대로(좌표·크기 산식은 어느 쪽이든 같다 — 바뀌는 건 시간 함수뿐). */
  motionKit?: boolean;
}

/** 🧰 레이아웃이 헬퍼에 넘기는 부품 판정 — prop 하나를 옵션 하나로(fps 는 실제 컴포지션 값). */
const kitOptOf = (p: LayoutBaseProps, fps: number): MotionKitOpt => ({ kit: p.motionKit === true, fps });

/** 🧷 소품(체크 표시·번호 원·✕✓ 표) 스프링 «톡» — kit 이고 아직 착지 전일 때만 transform 을 단다.
 *  착지 뒤·종전 경로는 `undefined` 라 스프레드해도 HTML 이 종전과 같다(«완성 프레임 = 종전» 검사의 근거). */
const propPopStyle = (opt: MotionKitOpt, f: number, enterAt: number): React.CSSProperties | undefined => {
  if (!opt.kit) return undefined;
  const sc = propPop(f, { enterAt, fps: opt.fps });
  return sc === 1 ? undefined : { transform: `scale(${sc})` };
};

/** 🧰 마지막 항목의 노출 하한(프레임) — kit 일 때만 값이 있다. 항목 글자로 `minHoldSec` 을 잰다. */
const holdFramesOf = (opt: MotionKitOpt, lastText: string | undefined): number | undefined =>
  opt.kit ? Math.ceil(minHoldSec(lastText ?? '') * (opt.fps ?? 30)) : undefined;

/* 👀 2차 «보이는 부품» 배선(2026-09-30) — 전부 kit 일 때만 값이 바뀌고, 종전 경로는 인자를 그대로 돌려준다. */

/** 글자 덩이는 kit 에서 불투명으로 두고 이동만 남긴다(어절 등장 `KitWords` 가 켜짐을 맡는다). 완성 프레임은 둘 다 opacity 1 이라 같다. */
const kitHold = <A extends { opacity: number; transform: string }>(a: A, k: MotionKitOpt): A => (k.kit ? { ...a, opacity: 1 } : a);

/** 키워드 강조 시작 프레임 — kit 이면 어절 등장이 끝난 뒤부터(끝나기 전엔 원문 노드가 없다). 종전은 호출부 값 그대로. */
const kwF = (k: MotionKitOpt, f: number, text: string, at: number, classic: number): number =>
  k.kit ? f - wordsDoneAt(text, at, k.fps) : classic;

/** 소품 «딸깍» 은 항목 착지(300ms) 뒤에 찍힌다. */
const clickAt = (k: MotionKitOpt, enterAt: number): number => enterAt + kitFrames(300, k.fps);

/** revealMode → i번째 요소 enterAt 프레임 (§3-11: sequential 25f · cascade 18→15→12→8 가속) */
export const enterAtFor = (index: number, revealMode: RevealMode = 'all', base = 6): number => {
  if (revealMode === 'sequential') return base + index * 25;
  if (revealMode === 'cascade') {
    const gaps = [18, 15, 12, 8];
    let at = base;
    for (let i = 0; i < index; i++) at += gaps[Math.min(i, gaps.length - 1)];
    return at;
  }
  return base;
};

/* ─── 🕐 장표 안 등장 리듬 — 항목이 «씬 내내» 하나씩 나오게 ──────────────────────────
 * ★왜(2026-09-16 실측)★ 종전엔 항목 간격이 25프레임 고정이라, 체크리스트 5항목이면 **4.53초**에
 *   전부 자리를 잡았다. 그런데 우리 씬은 평균 **11.2초**다 — 남은 **6.7초(씬의 60%)** 동안
 *   새로 나타나는 것이 없었다. 지속 모션(#4821·#4844)이 «살아 있게» 했다면, 이건 «바뀌게» 한다.
 * ★근거 데이터는 이미 있었다★ — `Overlay169Template` 이 그 씬의 나레이션 문장별 시작 프레임을
 *   이미 계산하고 있었는데 `type !== 'kineticTypo'` 면 버리고 있었다. 그 빗장만 풀어 받아 쓴다.
 * ★정직하게★ «항목 i = 문장 i» 라는 보장은 없다(디렉터가 따로 쓴다). 그래서 의미를 맞추는 게
 *   아니라, 항목을 고르게 편 뒤 **가장 가까운 문장 경계로 붙인다** — 항목이 문장 도중에 튀어나오지
 *   않게 하는 것이 여기서 얻는 것이다.
 */

/** 장표가 쓰는 시간표의 최소 형태. ★`kinetic-typo.KineticBeat` 를 import 하지 않는다★ —
 *  그 파일이 이 파일의 `LayoutBaseProps` 를 import 하므로 순환이 된다. 구조 호환이면 충분하다. */
export interface SlideBeat { atFrame: number; untilFrame?: number }

/** 항목은 씬의 앞 80% 안에 전부 나온다 — 뒤는 «다 읽을 시간»으로 남긴다. */
const REVEAL_SPAN = 0.8;
/** 항목 간 간격 하한 — 이보다 좁으면 «한꺼번에 나왔다»로 보인다. */
const MIN_GAP = 10;
/** 항목 간 간격 상한(2초) — 문장 경계가 없을 때만 쓴다. 더 벌리면 «다음 게 안 나오나» 싶어진다.
 *  ★문장 경계를 쓸 때는 이 상한을 걸지 않는다★ — 그 간격은 실제 나레이션 길이라 늘어난 게 맞다. */
const MAX_GAP = 60;

const _clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** 씬이 끝나는 씬 상대 프레임 — 마지막 구간의 `untilFrame` 과 씬 길이 중 **큰 쪽**.
 *  ★큰 쪽을 쓴다★ — 시간표가 씬 끝까지 안 채워져 있을 수 있고(마지막 문장 뒤 여백·잘린 시간표),
 *  그때 작은 쪽을 믿으면 항목이 씬 앞부분에 다시 몰린다(실측으로 잡은 함정). */
const sceneEndOf = (beats: ReadonlyArray<SlideBeat>, sceneFrames?: number): number | undefined => {
  const until = beats[beats.length - 1]?.untilFrame;
  const cands = [until, sceneFrames].filter(
    (v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
  return cands.length ? Math.max(...cands) : undefined;
};

/** 오름차순 + 최소 간격 보장. 문장 경계가 몰려 있어도 두 항목이 같은 프레임에 터지지 않는다. */
const _monotone = (frames: number[], base: number, ceil: number): number[] => {
  let prev = -Infinity;
  return frames.map((v) => {
    const at = Math.round(Math.min(ceil, Math.max(base, v, prev + MIN_GAP)));
    prev = at;
    return at;
  });
};

/**
 * 항목 `count` 개가 각각 등장할 씬 상대 프레임. 네 겹 우선순위:
 *  ⓪ `cues` 낱말 신호(2026-10-04) — 그 항목을 말하는 프레임. 찾은 항목만 그 자리에, 못 찾은 항목은 아래 ①~③ 값을
 *     이웃 신호 사이로 끼운다. «앞 80% 마감»은 걸지 않는다(말하는 순간이 정답이다) · 단조·최소 간격은 지킨다.
 *  ① `beats` 문장 경계가 항목 수만큼 있으면 그 위에 세운다(항목이 문장 도중에 안 나온다).
 *  ② 없거나 모자라면 `sceneFrames` 씬 길이에 고르게 편다(간격 상·하한 안에서).
 *  ③ 둘 다 없으면 **종전 그대로**(`enterAtFor` 고정 간격) — 기존 작업물 호환.
 * `revealMode:'all'` 은 «한꺼번에 보여 준다»는 디렉터의 연출이라 절대 늘리지 않는다.
 */
export function itemEnterFrames(
  count: number,
  opts: { beats?: ReadonlyArray<SlideBeat>; sceneFrames?: number; revealMode?: RevealMode; base?: number;
    /** 🧰 마지막 항목이 씬 끝까지 최소 이만큼(프레임) 보이도록 마감을 당긴다(부품 상자 노출 하한 · kit 만 준다). 미지정=종전. */
    holdFrames?: number;
    /** ⏱🔤 낱말 신호(⓪) — 길이가 count 와 같고 하나라도 숫자일 때만 쓴다. */
    cues?: ReadonlyArray<number | null> } = {},
): number[] {
  const { cues, ...rest } = opts;
  const planned = itemEnterFramesPlanned(count, rest);
  const { revealMode = 'sequential', base = 6 } = rest;
  if (!cues || cues.length !== count || revealMode === 'all' || !cues.some((v) => typeof v === 'number' && Number.isFinite(v))) return planned;
  // ⓪ 낱말 신호 — 찾은 항목은 그 프레임에 ★고정★하고, 못 찾은 항목은 앞뒤 신호 사이에 고르게 끼운다.
  //   ★신호를 밀지 않는다★ — 종전 값을 끼운 뒤 최소 간격으로 밀면 못 찾은 항목이 뒤의 신호를 늦춘다
  //   (dev 실측 «설명 못 한 돈» 0.57초 늦음). 신호끼리 10프레임보다 가까우면 그때만 1프레임씩 뒤로 비켜 선다.
  //   낱말 신호가 있는 장표는 하한을 6프레임까지 낮춘다 — 장면 첫 낱말이 곧 첫 항목이면(«소득 신고 내역») 제목 자리 잡기
  //   (18프레임)를 기다리느라 말보다 0.6초 늦었다. 무대 들어오기(쓸림 0.22초) 뒤면 제목과 함께 떠도 겹치지 않는다.
  const end = sceneEndOf(rest.beats ?? [], rest.sceneFrames);
  //   🧰 노출 하한(holdFrames · 부품 상자)은 지킨다 — 씬 끝에 말한 항목이 읽을 틈 없이 사라지지 않게(그 항목만 말보다 이르게 뜬다).
  const floor = Math.min(base, CUE_BASE_FRAMES);
  const ceil = typeof end === 'number' ? Math.max(floor, end - Math.max(1, rest.holdFrames ?? 0)) : Number.POSITIVE_INFINITY;
  const hit = (i: number) => { const v = cues[i]; return typeof v === 'number' && Number.isFinite(v) ? Math.min(ceil, Math.max(floor, Math.round(v))) : null; };
  const out: number[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const h = hit(i);
    if (h !== null) { out[i] = h; continue; }
    // 못 찾은 항목이 이어진 구간 [i, j) — 왼쪽 닻(앞 신호 · 없으면 하한)과 오른쪽 닻(뒤 신호) 사이에 고르게.
    let j = i;
    while (j < count && hit(j) === null) j++;
    const left = i > 0 ? out[i - 1] : null;
    const right = j < count ? hit(j)! : null;
    const len = j - i;
    for (let k = 0; k < len; k++) {
      if (right !== null) {
        out[i + k] = left === null
          ? Math.round(floor + ((right - floor) * k) / len)              // 첫 신호 앞: [하한, 신호) 를 고르게
          : Math.round(left + ((right - left) * (k + 1)) / (len + 1));    // 두 신호 사이: 사이를 고르게
      } else {
        // 마지막 신호 뒤: 종전 계획을 따르되 앞보다 최소 간격 뒤.
        const prev = k === 0 ? (left ?? floor - MIN_GAP) : out[i + k - 1];
        out[i + k] = Math.min(ceil, Math.max(planned[i + k], prev + MIN_GAP));
      }
    }
    i = j - 1;
  }
  // 같은 프레임에 둘이 터지지 않게만(1프레임씩) — 신호 자리는 사실상 그대로다.
  for (let i = 1; i < count; i++) if (out[i] <= out[i - 1]) out[i] = out[i - 1] + 1;
  return out;
}

/** 낱말 신호가 있을 때 항목이 뜰 수 있는 가장 이른 씬 상대 프레임(≈ 무대 쓸림 0.2초). */
const CUE_BASE_FRAMES = 6;

/** 종전 ①~③ 계획(낱말 신호 없이) — `itemEnterFrames` 가 ⓪ 의 빈칸 채우기·폴백으로 쓴다. */
function itemEnterFramesPlanned(
  count: number,
  opts: { beats?: ReadonlyArray<SlideBeat>; sceneFrames?: number; revealMode?: RevealMode; base?: number; holdFrames?: number },
): number[] {
  const { beats = [], sceneFrames, revealMode = 'sequential', base = 6, holdFrames } = opts;
  if (count <= 0) return [];
  const legacy = () => Array.from({ length: count }, (_, i) => enterAtFor(i, revealMode, base));
  if (revealMode === 'all' || count === 1) return legacy();

  const end = sceneEndOf(beats, sceneFrames);
  if (typeof end !== 'number' || end <= base) return legacy();
  // 마지막 항목이 늦어도 여기까지는 나온다. 🧰 노출 하한이 있으면 그만큼 더 당긴다(base 아래로는 안 내려간다).
  const deadline = Math.max(base, Math.min(base + (end - base) * REVEAL_SPAN, end - (holdFrames ?? 0)));

  // ① 문장 경계 위에 세운다 — 문장이 항목 수 이상일 때.
  //   ★마감으로 «걸러내지» 않는다★ — 거르면 쓸 문장이 모자라 ②로 떨어진다(첫 구현의 결함:
  //   문장 5·항목 5 인데도 ① 이 한 번도 안 탔다). 대신 고른 값을 마감으로 **당겨 붙인다**.
  const usable = beats.filter((b) => Number.isFinite(b.atFrame));
  if (usable.length >= count) {
    const picked = Array.from({ length: count }, (_, i) =>
      usable[Math.round((i * (usable.length - 1)) / (count - 1))].atFrame);
    return _monotone(picked, base, deadline);
  }

  // ② 씬 길이에 고르게. 종전(25f 고정)보다 넓어지되 상·하한 안에서.
  const gap = _clamp((deadline - base) / (count - 1), MIN_GAP, MAX_GAP);
  return _monotone(Array.from({ length: count }, (_, i) => base + i * gap), base, deadline);
}

/** 실제 해상도 → 가장 가까운 LAYOUT aspect 판별 */
export const inferAspect = (width: number, height: number): MsbAspect => {
  const r = width / height;
  const candidates: Array<[MsbAspect, number]> = [
    ['16:9', 16 / 9],
    ['9:16', 9 / 16],
    ['4:5', 4 / 5],
  ];
  candidates.sort((a, b) => Math.abs(r - a[1]) - Math.abs(r - b[1]));
  return candidates[0][0];
};

/**
 * 안전 영역 px — 세이프존 정본(`@/lib/safe-zone` · 데드존 가이드 실측)의 `core` × 실해상도.
 * ★좌우가 다르다★ — 세로 화면은 오른쪽에 좋아요·댓글·공유 버튼 열이 있어 오른쪽이 왼쪽의 2.7배다
 *   (9:16 좌 5.53% · 우 14.75%). 종전 `safeS` 한 값으로는 이 축을 표현조차 못 해서 `safeL`·`safeR` 로 갈랐다.
 * ★`core` 인 이유★ — 장표는 전부 «읽는 글자»다. 빗금 층(제목·로고처럼 잘려도 뜻이 사는 것)에 둘 요소가 없다.
 */
export const useSafeArea = (aspect?: MsbAspect) => {
  const { width, height, fps } = useVideoConfig();
  const a = aspect ?? inferAspect(width, height);
  // 🧊 «모션 브리핑» 무대 안이면 도형 안쪽이 곧 안전 영역이다(무대가 도형을 세이프존 core 안에 둔다 · 없으면 종전).
  const stage = React.useContext(SlideStageInsetContext);
  const safe = stage ?? safeInsetPx(a, width, height, 'core');
  return {
    aspect: a,
    W: width,
    H: height,
    /** 컴포지션 fps — 부품 상자의 ms 기본값을 프레임으로 바꿀 때 쓴다. */
    fps,
    safeT: safe.top,
    safeB: safe.bottom,
    safeL: safe.left,
    safeR: safe.right,
    /** 토큰 px 스케일 — T 스케일은 1920×1080 기준. 다른 해상도·비율에서는 폭 비례 축소 */
    px: (v: number) => Math.round(v * (a === '16:9' ? width / 1920 : width / 1080)),
  };
};

const _anchorFlex = (anchor: Anchor): React.CSSProperties => ({
  justifyContent: anchor.startsWith('top') ? 'flex-start' : anchor.startsWith('bottom') ? 'flex-end' : 'center',
  alignItems: anchor.endsWith('left') ? 'flex-start' : anchor.endsWith('right') ? 'flex-end' : 'center',
  textAlign: anchor.endsWith('left') ? 'left' : anchor.endsWith('right') ? 'right' : 'center',
});

/* ─── 🎞 장표 지속 모션 — 등장이 끝난 뒤에도 화면이 멈추지 않는다 ────────────────────
 * ★왜(2026-09-16 실측)★ 장표 10종 중 등장 뒤에도 움직이는 것은 모션 타이포뿐이었고 나머지 9종은
 *   «미끄러져 들어온 뒤 완전히 멈춘 그림»이었다. 게다가 우리 씬은 평균 11.2초를 한 장이 버틴다
 *   (모션 그래픽 레퍼런스의 컷당 2.02초 대비 5.5배) — 멈춘 그림이 11초를 서 있으면 정지 화면이다.
 * ★제약: 장표는 «읽는» 화면★ 이라 모션 타이포의 글자별 바운스·물결을 그대로 옮기지 않는다
 *   (W1 «읽는 장표에 글자 바운스는 소음»). 쓰는 것은 두 가지뿐 —
 *     ① `sustainedPushIn` 카메라를 8초에 걸쳐 1.0→1.035 로 아주 천천히 민다(덩어리 단위 1회).
 *     ② `sustainedFloat` 항목마다 **위상을 달리해** 미세하게 띄운다(주기 ≈7초 · 진폭 ±3~4px).
 * ★규율 3가지★
 *   - `slideIn().transform` 을 **덮어쓰지 말고 이어 붙인다**(`${anim.transform} …`). 덮으면 등장이 죽는다.
 *   - 부유는 **등장이 끝난 프레임부터**(`enterAt + duration`). 착지 중에 흔들면 «덜컹»으로 보인다.
 *   - 프레임만의 함수로 둔다 — Lambda 가 프레임 구간을 워커로 쪼개므로 난수·상태는 청크 경계에서 튄다.
 * 원함수 정의·근거는 `motion.tsx` 의 sustained 절. */

/** 지속 부유 진폭(360 기준 px — 호출부에서 `s.px` 로 해상도 스케일).
 *  글자 줄은 작게, 카드·패널은 조금 크게 — 글자가 크게 흔들리면 읽는 데 방해가 된다. */
const FLOAT_AMP = { text: 3, panel: 4 } as const;

/** 🧊 무대 안 «칸 먼저» — 차례로 들어오는 칸(단계·카드)은 들어오기 전에도 테두리를 흐리게(0.35) 깔아 둔다.
 *  아직 안 들어온 칸 자리가 «빈 1/3»로 보였다(09-30 회장님 지적 · 주제1 47초). 글자는 종전처럼 들어올 때 켜진다(안쪽 불투명도 = 등장값 ÷ 칸 불투명도).
 *  무대 밖(`on` 거짓)은 종전 그대로 — 칸째 등장값을 쓴다. */
const CELL_GHOST = 0.35;
const cellOpacity = (on: boolean, enter: number): { outer: number; inner: number } => {
  if (!on) return { outer: enter, inner: 1 };
  const outer = Math.max(enter, CELL_GHOST);
  return { outer, inner: outer > 0 ? enter / outer : 0 };
};

/** 레이아웃 공통 컨테이너 — 안전 영역 padding + anchor flex 배치 */
const Frame: React.FC<{ anchor: Anchor; aspect?: MsbAspect; children: React.ReactNode }> = ({ anchor, aspect, children }) => {
  const { safeT, safeB, safeL, safeR } = useSafeArea(aspect);
  return (
    <AbsoluteFill
      style={{
        paddingTop: safeT, paddingBottom: safeB, paddingLeft: safeL, paddingRight: safeR,
        display: 'flex', flexDirection: 'column', ..._anchorFlex(anchor),
      }}
    >
      {children}
    </AbsoluteFill>
  );
};

// ─── 1. FullText — 타이틀·키 메시지 (1 유닛: title+sub) ────────────

export const FullText: React.FC<LayoutBaseProps & { title: string; sub?: string }> = (p) => {
  const { colors: C, f, revealMode = 'all', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const k = kitOptOf(p, s.fps);
  const titleEnter = enterAtFor(0, revealMode);
  const subEnter = enterAtFor(1, revealMode, 14);
  const t = slideIn(f, { enterAt: titleEnter, duration: 24, ...k });
  const sub = slideIn(f, { enterAt: subEnter, duration: 20, ...k });
  // 📐 #6394 큰 제목 마지막 줄에 한 어절(«X»·«체결»)만 남으면 실제 글꼴로 재서 고르게 다시 나눈다(줄 수·글자 그대로 · 고른 제목은 종전 바이트).
  const [titleRef, title] = useBalancedTitle(p.title, { avail: 'parent', keyword: p.keyword });
  const push = sustainedPushIn(f);
  // 📐 #6119 2차 «한 줄로» — 부제가 줄바꿈 목록(4줄 이상)이면 두 열로 나눠 세로 자리를 반으로(16:9 · 넘칠 때만). 그 밖은 종전 한 덩이.
  //   #6354 모션 무대도 같은 두 열·촘촘한 줄 높이를 요구한다(무대가 자막 윗선 위에 안 들어갈 때만 · 무대 밖은 둘 다 false = 종전).
  const room = useSlideRoom();
  const stageCompact = useStageCompact();
  const stageCols = useStageSubCols();
  const subCols = (room >= 2 || stageCols) && s.aspect === '16:9' ? briefFullTextSubCols(p.sub) : null;
  const subAt = k.kit ? Math.max(subEnter, wordsDoneAt(p.title, titleEnter, k.fps) - kitFrames(200, k.fps)) : subEnter;
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      <div ref={titleRef} style={{ ...kitHold(t, k),
        transform: `${t.transform} translateY(${sustainedFloat(f, 0, titleEnter + motionEnterFrames(k, 24), s.px(FLOAT_AMP.text))}px) scale(${push})`,
        fontSize: s.px(T.hero), fontWeight: FW.bold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, ...KO_TEXT }}>
        <KitWords kit={k.kit} fps={k.fps} f={f} at={titleEnter} text={title}>
          <KeywordText text={title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, title, titleEnter, f - enterAtFor(0, revealMode) - 16)} />
        </KitWords>
      </div>
      {p.sub ? (
        <div style={{ ...kitHold(sub, k),
          transform: `${sub.transform} translateY(${sustainedFloat(f, 1, subEnter + motionEnterFrames(k, 20), s.px(FLOAT_AMP.text))}px) scale(${push})`,
          marginTop: s.px(28), fontSize: s.px(T.body), fontWeight: FW.regular, lineHeight: stageCompact ? BRIEF_COMPACT.lineHeight : LINE_HEIGHT.normal, color: C.textSub, ...KO_TEXT,
          ...(subCols ? { display: 'flex', justifyContent: 'center', gap: s.px(BRIEF_SUB_COL_GAP), textAlign: 'left' as const } : {}) }}>
          {subCols
            ? subCols.map((col, j) => <div key={j}><KitWords kit={k.kit} fps={k.fps} f={f} at={subAt} text={col}>{col}</KitWords></div>)
            : <KitWords kit={k.kit} fps={k.fps} f={f} at={subAt} text={p.sub}>{p.sub}</KitWords>}
        </div>
      ) : null}
    </Frame>
  );
};

// ─── 2. CardGrid — 카드 비교·나열 (cols=2: 2~4 · cols=3: 3) ────────

export interface CardItem { badge?: string; title: string; body?: string[] }

export const CardGrid: React.FC<LayoutBaseProps & {
  cols: 2 | 3;
  cards: CardItem[];
  title?: string;
}> = (p) => {
  const { colors: C, f, revealMode = 'sequential', anchor = 'center', cardStyle = 'filled' } = p;
  /* 📐 ★cols 가 없어도 NaN 을 만들지 않는다★(2026-09-18 렌더 실측) — story 트랙(Overlay169)은 씬 content 를
     **그대로 스프레드**해 넘기므로 `cols` 가 비어 들어올 수 있다(MSB 트랙은 composition.tsx 가 보정한다).
     그때 `Math.min(undefined, 3)` = NaN → `flex: calc(NaN% …)` 로 카드가 쪼그라들어 한쪽으로 밀렸다.
     여기서 한 번 더 받는다 — 값 목록(2·3) 밖이면 2. */
  const cols: 2 | 3 = p.cols === 3 ? 3 : 2;
  const s = useSafeArea(p.aspect);
  const hideTitle = useStageHidesTitle(); // 🧊 모션 브리핑 무대가 제목을 도형 위에 따로 띄우면 여기선 그리지 않는다
  const [titleRef, title] = useBalancedTitle(p.title, { avail: 'parent', keyword: p.keyword }); // 📐 #6394 마지막 줄 한 어절 막기(실측 · 고른 제목은 그대로)
  const mk = useStageMarks();
  // 🧊 무대 칸 계획 — 카드 제목이 한 줄로 드는 한 줄 카드 수·제목 크기(무대 밖 undefined = 종전).
  const cells = useStageCells();
  // 🧊 무대가 «촘촘히»를 요구하면(보통 간격으로 도형에 안 들어갈 때만) 칸 위아래 여백·간격·줄 높이부터 줄인다(글자·가로 여백 그대로).
  // 📐 #6119 2차 — 무대 밖에서 자막 위 칸에 안 들어가면 장표 층이 단계를 올린다: 1 = 무대의 «촘촘히»와 같은 칸 값 · 2 = 16:9 카드 4장 이하를 한 줄로.
  const roomAsked = useSlideRoom(); // 훅은 조건 없이 먼저 읽는다
  const room = cells ? 0 : roomAsked;
  const tight = useStageCompact() || room >= 1 ? BRIEF_COMPACT_CELL : null;
  const gridGap = s.px(room >= 1 ? 20 : 32);
  const k = kitOptOf(p, s.fps);
  // §8-0: 한 행 카드 수 상한 — 16:9 ≤3 · 9:16/4:5 ≤2 (초과분은 wrap)
  const basePerRow = cells ? cells.perRow : Math.min(cols, s.aspect === '16:9' ? 3 : 2);
  const perRow = room >= 2 && s.aspect === '16:9' && p.cards.length > basePerRow && p.cards.length <= 4 ? p.cards.length : basePerRow;
  const titleAnim = slideIn(f, { enterAt: 4, duration: 20, ...k });
  const titleSettled = 4 + motionEnterFrames(k, 20);
  const push = sustainedPushIn(f);
  const enterOpts = { beats: p.beats, sceneFrames: p.sceneFrames, revealMode, base: p.title ? 18 : 6 };
  const enters = itemEnterFrames(p.cards.length, { ...enterOpts, cues: p.itemCues,
    holdFrames: holdFramesOf(k, p.cards.length ? [p.cards[p.cards.length - 1].title, ...(p.cards[p.cards.length - 1].body ?? [])].join(' ') : undefined) });
  // 부유 위상은 종전 등장 시각 기준 — 노출 하한이 항목을 당겨도 완성 프레임은 종전과 픽셀까지 같다.
  const floatEnters = k.kit ? itemEnterFrames(p.cards.length, enterOpts) : enters;
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      {p.title && !hideTitle ? (
        <div ref={titleRef} style={{ ...kitHold(titleAnim, k),
          transform: `${titleAnim.transform} translateY(${sustainedFloat(f, 0, titleSettled, s.px(FLOAT_AMP.text))}px) scale(${push})`,
          fontSize: s.px(T.header), fontWeight: FW.semibold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, marginBottom: s.px(room >= 1 ? 24 : 48), ...KO_TEXT }}>
          <KitWords kit={k.kit} fps={k.fps} f={f} at={4} text={title}>
            <KeywordText text={title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, title, 4, f - 20)} />
          </KitWords>
        </div>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: gridGap, alignItems: 'stretch', justifyContent: 'center', width: '100%', transform: `scale(${push})` }}>
        {p.cards.map((card, i) => {
          const cardEnterAt = enters[i];
          const anim = slideIn(f, { enterAt: cardEnterAt, duration: 20, ...k });
          const op = cellOpacity(!!cells, anim.opacity);
          const inner = op.inner < 1 ? { opacity: op.inner } : {};
          return (
            <div
              key={i}
              {...mk.cell}
              style={{
                ...(CARD(cardStyle, C) as object), ...anim, opacity: op.outer,
                ...(tight ? { padding: `${tight.cardPadY}px 28px` } : {}),
                transform: `${anim.transform} translateY(${sustainedFloat(f, i + 1, floatEnters[i] + motionEnterFrames(k, 20), s.px(FLOAT_AMP.panel))}px)`,
                flex: `1 1 calc(${100 / perRow}% - ${gridGap}px)`,
                maxWidth: `calc(${100 / perRow}% - ${s.px(16)}px)`,
                minHeight: tight ? 0 : s.px(180),
                display: 'flex', flexDirection: 'column', gap: s.px(tight ? tight.cardGap : 14), textAlign: 'left',
              }}
            >
              {card.badge ? (
                <div {...mk.label} style={{ ...inner, fontSize: s.px(T.label), fontWeight: FW.bold, color: C.accent, ...KO_TEXT }}>{card.badge}</div>
              ) : null}
              <div {...mk.label} style={{ ...inner, fontSize: s.px(cells ? cells.labelPx : T.body), fontWeight: FW.bold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, ...KO_TEXT }}>
                <KitWords kit={k.kit} fps={k.fps} f={f} at={cardEnterAt} text={card.title}>
                  <KeywordText text={card.title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, card.title, cardEnterAt, f - cardEnterAt - 12)} />
                </KitWords>
              </div>
              {(card.body ?? []).map((line, j) => (
                <div key={j} style={{ ...inner, fontSize: s.px(T.caption), fontWeight: FW.regular, lineHeight: tight ? tight.cardBodyLine : LINE_HEIGHT.loose, color: C.textSub, ...KO_TEXT }}>
                  <KitWords kit={k.kit} fps={k.fps} f={f} at={cardEnterAt + kitFrames(150 * (j + 1), k.fps)} text={line}>{line}</KitWords>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

// ─── 3. CheckList — 체크 항목 (3~6) ────────────────────────────────

/** 🧍✅ 진행자 구도(상반신 아바타)의 체크 — 줄은 먼저 다 보이고 체크 칸만 «그 항목을 말하는» 순간 초록으로 켜진다(v3 아침 조회). */
const TICK_ON = '#249158';
const TICK_OFF = 'rgba(96,106,120,0.55)';

export const CheckList: React.FC<LayoutBaseProps & { title?: string; items: string[]; tickOnCue?: boolean }> = (p) => {
  const { colors: C, f, revealMode = 'sequential', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const hideTitle = useStageHidesTitle(); // 🧊 모션 브리핑 무대가 제목을 도형 위에 따로 띄우면 여기선 그리지 않는다
  const [titleRef, title] = useBalancedTitle(p.title, { avail: 'parent', keyword: p.keyword }); // 📐 #6394 마지막 줄 한 어절 막기(실측 · 고른 제목은 그대로)
  const k = kitOptOf(p, s.fps);
  const titleAnim = slideIn(f, { enterAt: 4, duration: 20, ...k });
  const titleSettled = 4 + motionEnterFrames(k, 20);
  const push = sustainedPushIn(f);
  const enterOpts = { beats: p.beats, sceneFrames: p.sceneFrames, revealMode, base: p.title ? 18 : 6 };
  const enters = itemEnterFrames(p.items.length, { ...enterOpts, cues: p.itemCues,
    holdFrames: holdFramesOf(k, p.items[p.items.length - 1]) });
  // 🧍✅ tickOnCue — 줄은 제목 뒤 짧은 간격으로 먼저 다 서고(rowEnters), 체크만 enters(낱말 시각)에 켜진다. 미지정 = 종전(줄과 체크가 함께 등장).
  const tick = p.tickOnCue === true;
  const rowEnters = tick ? p.items.map((_, i) => enterOpts.base + i * 4) : enters;
  // 부유 위상은 종전 등장 시각 기준 — 노출 하한이 항목을 당겨도 완성 프레임은 종전과 픽셀까지 같다.
  const floatEnters = tick ? rowEnters : k.kit ? itemEnterFrames(p.items.length, enterOpts) : enters;
  // 🧹 화면이 ✓ 를 붙이므로 앞머리 ✅·✔ 는 뗀다. 등장 시각(낱말 신호)은 원문 기준 그대로.
  const shown = p.items.map((it) => stripLeadingMark(it, 'check'));
  // 🖱 «모션 브리핑» 무대 안에서만 커서가 체크 칸을 누른다(착수문 «커서는 선택·체크가 있는 장면에만»). 무대 밖은 종전 HTML 그대로.
  const onStage = React.useContext(SlideStageInsetContext) !== null; // 훅은 조건 없이 먼저 읽는다
  const cursorOn = k.kit === true && onStage;
  // 🧊 무대가 «촘촘히»를 요구하면(보통 간격으론 도형에 안 들어갈 때만) 항목 간격 10 · 줄 높이 1.3 — 글자를 줄이기 전에 빈틈부터 줄인다.
  // 📐 #6119 2차 — 자막 위 칸에 안 들어가면 장표 층이 «촘촘히»(1)를 건다 — 무대의 촘촘한 목록과 같은 값.
  const room = useSlideRoom();
  const compact = useStageCompact() || room >= 1;
  const mk = useStageMarks();
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      {p.title && !hideTitle ? (
        <div ref={titleRef} style={{ ...kitHold(titleAnim, k),
          transform: `${titleAnim.transform} translateY(${sustainedFloat(f, 0, titleSettled, s.px(FLOAT_AMP.text))}px) scale(${push})`,
          fontSize: s.px(T.header), fontWeight: FW.semibold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, marginBottom: s.px(44), ...KO_TEXT }}>
          <KitWords kit={k.kit} fps={k.fps} f={f} at={4} text={title}>
            <KeywordText text={title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, title, 4, f - 20)} />
          </KitWords>
        </div>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: s.px(compact ? 10 : 24), width: '100%', maxWidth: s.px(1280), transform: `scale(${push})` }}>
        {p.items.map((_item, i) => {
          const itemEnterAt = enters[i];
          const anim = slideIn(f, { enterAt: rowEnters[i], duration: 18, ...k });
          const ticked = !tick || f >= itemEnterAt;
          return (
            <div key={i} style={{ ...anim,
              transform: `${anim.transform} translateY(${sustainedFloat(f, i + 1, floatEnters[i] + motionEnterFrames(k, 18), s.px(FLOAT_AMP.text))}px)`,
              display: 'flex', alignItems: 'center', gap: s.px(24), textAlign: 'left', ...(cursorOn ? { position: 'relative' as const } : {}) }}>
              <div
                style={{
                  width: s.px(52), height: s.px(52), borderRadius: s.px(12), flexShrink: 0,
                  border: `${Math.max(2, s.px(3))}px solid ${tick ? (ticked ? TICK_ON : TICK_OFF) : C.accent}`, color: tick ? '#FFFFFF' : C.accent,
                  ...(tick && ticked ? { background: TICK_ON } : {}),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: s.px(T.label), fontWeight: FW.bold,
                  // 🧷 kit — 체크 표시만 소품 스프링으로 «톡»(글자는 튀지 않는다). 종전·착지 뒤엔 아무것도 안 단다.
                  ...propPopStyle(k, f, itemEnterAt),
                }}
              >
                {ticked ? <KitPop kit={k.kit} fps={k.fps} f={f} at={clickAt(k, itemEnterAt)}>✓</KitPop> : null}
              </div>
              <div {...mk.label} style={{ fontSize: s.px(T.body), fontWeight: FW.medium, lineHeight: compact ? 1.3 : LINE_HEIGHT.normal, color: C.textMain, ...KO_TEXT }}>
                <KitWords kit={k.kit} fps={k.fps} f={f} at={itemEnterAt} text={shown[i]}>
                  {/* 강조 낱말이 든 항목은 종전 그대로(강조 상자가 글을 가른다) · 아니면 숫자+단위 덩이를 한 상자로. */}
                  {p.keyword && shown[i].includes(p.keyword)
                    ? <KeywordText text={shown[i]} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, shown[i], itemEnterAt, f - itemEnterAt - 10)} />
                    : <NumRunText text={shown[i]} />}
                </KitWords>
              </div>
              {cursorOn ? <KitCursor f={f} fps={k.fps} at={clickAt(k, itemEnterAt)} x={s.px(26)} px={s.px} /> : null}
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

// ─── 4. StatRow — KPI 가로 나열 (3~4) ──────────────────────────────

export interface StatItem { value: string; label: string }

export const StatRow: React.FC<LayoutBaseProps & { title?: string; stats: StatItem[] }> = (p) => {
  const { colors: C, f, revealMode = 'sequential', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const hideTitle = useStageHidesTitle(); // 🧊 모션 브리핑 무대가 제목을 도형 위에 따로 띄우면 여기선 그리지 않는다
  const [titleRef, title] = useBalancedTitle(p.title, { avail: 'parent', keyword: p.keyword }); // 📐 #6394 마지막 줄 한 어절 막기(실측 · 고른 제목은 그대로)
  const k = kitOptOf(p, s.fps);
  const titleAnim = slideIn(f, { enterAt: 4, duration: 20, ...k });
  const titleSettled = 4 + motionEnterFrames(k, 20);
  const push = sustainedPushIn(f);
  const enterOpts = { beats: p.beats, sceneFrames: p.sceneFrames, revealMode, base: p.title ? 18 : 6 };
  const enters = itemEnterFrames(p.stats.length, { ...enterOpts, cues: p.itemCues,
    holdFrames: holdFramesOf(k, p.stats.length ? `${p.stats[p.stats.length - 1].value} ${p.stats[p.stats.length - 1].label}` : undefined) });
  // 부유 위상은 종전 등장 시각 기준 — 노출 하한이 항목을 당겨도 완성 프레임은 종전과 픽셀까지 같다.
  const floatEnters = k.kit ? itemEnterFrames(p.stats.length, enterOpts) : enters;
  const mk = useStageMarks();
  // 🧹 값 칸 폭 = 안전 영역 안쪽 폭을 칸 수로 나눈 것(16:9 는 한 줄 고정 · 그 밖은 줄을 넘겨 쌓이므로 한 칸이 한 줄 전체까지 쓴다).
  //   종전 크기로 한 줄에 들어가는 값은 손대지 않는다(바이트 동일) — 안 들어가면 칸 폭에 맞춰 글자를 줄이고, 그래도 안 되면 줄을 직접 나눈다(숫자+단위는 한 덩이).
  const innerW = Math.max(0, s.W - s.safeL - s.safeR);
  const nStats = Math.max(1, p.stats.length);
  const statColW = s.aspect === '16:9' ? (innerW - s.px(48) * (nStats - 1)) / nStats : innerW;
  const valuePlans = p.stats.map((st) => planStatValue(st.value, statColW, s.px(T.hero), s.px(T.body)));
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      {p.title && !hideTitle ? (
        <div ref={titleRef} style={{ ...kitHold(titleAnim, k),
          transform: `${titleAnim.transform} translateY(${sustainedFloat(f, 0, titleSettled, s.px(FLOAT_AMP.text))}px) scale(${push})`,
          fontSize: s.px(T.header), fontWeight: FW.semibold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, marginBottom: s.px(56), ...KO_TEXT }}>
          <KitWords kit={k.kit} fps={k.fps} f={f} at={4} text={title}>
            <KeywordText text={title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, title, 4, f - 20)} />
          </KitWords>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: s.px(48), alignItems: 'stretch', justifyContent: 'center', width: '100%', flexWrap: s.aspect === '16:9' ? 'nowrap' : 'wrap', transform: `scale(${push})` }}>
        {p.stats.map((st, i) => {
          const statEnterAt = enters[i];
          const anim = slideIn(f, { enterAt: statEnterAt, duration: 20, ...k });
          return (
            // 수치 칸은 테두리 없는 열이라 «칸» 표식을 달지 않는다 — 경계는 도형이다(글자가 등장하며 열 아래로 몇 px 나오는 것은 도형 안).
            <div key={i} style={{ ...anim,
              transform: `${anim.transform} translateY(${sustainedFloat(f, i + 1, floatEnters[i] + motionEnterFrames(k, 20), s.px(FLOAT_AMP.panel))}px)`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: s.px(16), flex: '1 1 0', ...(valuePlans[i].fit ? { minWidth: 0 } : {}) }}>
              {(() => {
                const vp = valuePlans[i];
                if (!vp.fit) return (
                  <div {...mk.label} style={{ fontSize: s.px(T.hero), fontWeight: FW.extrabold, lineHeight: LINE_HEIGHT.tight, color: C.accent, ...KO_TEXT }}>
                    <KitCount kit={k.kit} fps={k.fps} f={f} at={statEnterAt} text={st.value} />
                  </div>
                );
                // 줄마다 따로 상자 · 줄바꿈 없음 — 내보내기 엔진이 줄 끝을 잘못 짚을 틈을 없앤다.
                return (
                  <div {...mk.label} style={{ fontSize: vp.fontPx, fontWeight: FW.extrabold, lineHeight: LINE_HEIGHT.tight, color: C.accent, ...KO_TEXT, whiteSpace: 'nowrap' }}>
                    {vp.lines.map((line, j) => (
                      <div key={j}><KitCount kit={k.kit} fps={k.fps} f={f} at={statEnterAt} text={line} /></div>
                    ))}
                  </div>
                );
              })()}
              <div {...mk.label} style={{ fontSize: s.px(T.caption), fontWeight: FW.medium, lineHeight: LINE_HEIGHT.normal, color: C.textSub, ...KO_TEXT }}>
                <KitWords kit={k.kit} fps={k.fps} f={f} at={statEnterAt + kitFrames(200, k.fps)} text={st.label}>{st.label}</KitWords>
              </div>
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

// ─── 5. BigNumber — 단일 강조 통계 (1) ─────────────────────────────

export const BigNumber: React.FC<LayoutBaseProps & { value: string; label?: string; sub?: string }> = (p) => {
  const { colors: C, f, revealMode = 'all', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const k = kitOptOf(p, s.fps);
  const valueEnter = enterAtFor(0, revealMode);
  const labelEnter = enterAtFor(1, revealMode, 16);
  const v = slideIn(f, { enterAt: valueEnter, duration: 22, ...k });
  const l = slideIn(f, { enterAt: labelEnter, duration: 20, ...k });
  const valueSettled = valueEnter + motionEnterFrames(k, 22);
  const labelSettled = labelEnter + motionEnterFrames(k, 20);
  const push = sustainedPushIn(f);
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      {p.label ? (
        <div style={{ ...kitHold(l, k),
          transform: `${l.transform} translateY(${sustainedFloat(f, 1, labelSettled, s.px(FLOAT_AMP.text))}px) scale(${push})`,
          fontSize: s.px(T.body), fontWeight: FW.medium, lineHeight: LINE_HEIGHT.normal, color: C.textSub, marginBottom: s.px(20), ...KO_TEXT }}>
          <KitWords kit={k.kit} fps={k.fps} f={f} at={labelEnter} text={p.label}>{p.label}</KitWords>
        </div>
      ) : null}
      <div style={{ ...v,
        transform: `${v.transform} translateY(${sustainedFloat(f, 0, valueSettled, s.px(FLOAT_AMP.panel))}px) scale(${push})`,
        fontSize: s.px(T.display), fontWeight: FW.extrabold, lineHeight: LINE_HEIGHT.tight, color: C.accent, ...KO_TEXT }}>
        <KitCount kit={k.kit} fps={k.fps} f={f} at={valueEnter} text={p.value} />
      </div>
      {p.sub ? (
        // 라벨과 같은 진입 애니(`l`)를 쓰지만 부유 위상은 달리한다 — 같은 위상이면 위아래가 한 덩어리로 흔들린다.
        <div style={{ ...kitHold(l, k),
          transform: `${l.transform} translateY(${sustainedFloat(f, 2, labelSettled, s.px(FLOAT_AMP.text))}px) scale(${push})`,
          marginTop: s.px(24), fontSize: s.px(T.body), fontWeight: FW.regular, lineHeight: LINE_HEIGHT.normal, color: C.textMain, ...KO_TEXT }}>
          <KitWords kit={k.kit} fps={k.fps} f={f} at={labelEnter + kitFrames(400, k.fps)} text={p.sub}>
            <KeywordText text={p.sub} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, p.sub, labelEnter + kitFrames(400, k.fps), f - 30)} />
          </KitWords>
        </div>
      ) : null}
    </Frame>
  );
};

// ─── 6. SplitFocus — 좌 라벨 + 우 본문 (1+1) ───────────────────────

export const SplitFocus: React.FC<LayoutBaseProps & { label: string; body: string; sub?: string }> = (p) => {
  const { colors: C, f, revealMode = 'sequential', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const k = kitOptOf(p, s.fps);
  const leftEnter = enterAtFor(0, revealMode);
  const rightEnter = enterAtFor(1, revealMode, 14);
  const left = slideIn(f, { enterAt: leftEnter, duration: 20, direction: 'right', ...k });
  const right = slideIn(f, { enterAt: rightEnter, duration: 20, direction: 'left', ...k });
  const sideSettle = motionEnterFrames(k, 20);
  const push = sustainedPushIn(f);
  const horizontal = s.aspect === '16:9';
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      <div style={{ display: 'flex', flexDirection: horizontal ? 'row' : 'column', gap: s.px(56), alignItems: 'center', width: '100%', maxWidth: s.px(1600), transform: `scale(${push})` }}>
        <div style={{ ...kitHold(left, k),
          transform: `${left.transform} translateY(${sustainedFloat(f, 0, leftEnter + sideSettle, s.px(FLOAT_AMP.panel))}px)`,
          flex: horizontal ? '0 0 36%' : undefined, textAlign: horizontal ? 'right' : 'center' }}>
          <div style={{ fontSize: s.px(T.title), fontWeight: FW.bold, lineHeight: LINE_HEIGHT.tight, color: C.accent, ...KO_TEXT }}>
            <KitWords kit={k.kit} fps={k.fps} f={f} at={leftEnter} text={p.label}>{p.label}</KitWords>
          </div>
        </div>
        <div style={{ alignSelf: 'stretch', ...(horizontal ? { width: 1, background: C.isDark === false ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } : (DIVIDER.rule as object)) }} />
        <div style={{ ...kitHold(right, k),
          transform: `${right.transform} translateY(${sustainedFloat(f, 1, rightEnter + sideSettle, s.px(FLOAT_AMP.text))}px)`,
          flex: '1 1 0', textAlign: 'left' }}>
          <div style={{ fontSize: s.px(T.body), fontWeight: FW.medium, lineHeight: LINE_HEIGHT.normal, color: C.textMain, ...KO_TEXT }}>
            <KitWords kit={k.kit} fps={k.fps} f={f} at={rightEnter} text={p.body}>
              <KeywordText text={p.body} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, p.body, rightEnter, f - enterAtFor(1, revealMode, 14) - 12)} />
            </KitWords>
          </div>
          {p.sub ? (
            <div style={{ marginTop: s.px(20), fontSize: s.px(T.caption), fontWeight: FW.regular, lineHeight: LINE_HEIGHT.loose, color: C.textSub, ...KO_TEXT }}>
              <KitWords kit={k.kit} fps={k.fps} f={f} at={k.kit ? wordsDoneAt(p.body, rightEnter, k.fps) - kitFrames(200, k.fps) : rightEnter} text={p.sub}>{p.sub}</KitWords>
            </div>
          ) : null}
        </div>
      </div>
    </Frame>
  );
};

// ─── 7. StepFlow — 단계 흐름 (3~5, 화살표) ─────────────────────────

export const StepFlow: React.FC<LayoutBaseProps & { title?: string; steps: string[] }> = (p) => {
  const { colors: C, f, revealMode = 'sequential', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const hideTitle = useStageHidesTitle(); // 🧊 모션 브리핑 무대가 제목을 도형 위에 따로 띄우면 여기선 그리지 않는다
  const [titleRef, title] = useBalancedTitle(p.title, { avail: 'parent', keyword: p.keyword }); // 📐 #6394 마지막 줄 한 어절 막기(실측 · 고른 제목은 그대로)
  const k = kitOptOf(p, s.fps);
  const titleAnim = slideIn(f, { enterAt: 4, duration: 20, ...k });
  const titleSettled = 4 + motionEnterFrames(k, 20);
  const push = sustainedPushIn(f);
  const enterOpts = { beats: p.beats, sceneFrames: p.sceneFrames, revealMode, base: p.title ? 18 : 6 };
  const enters = itemEnterFrames(p.steps.length, { ...enterOpts, cues: p.itemCues,
    holdFrames: holdFramesOf(k, p.steps[p.steps.length - 1]) });
  // 부유 위상은 종전 등장 시각 기준 — 노출 하한이 항목을 당겨도 완성 프레임은 종전과 픽셀까지 같다.
  const floatEnters = k.kit ? itemEnterFrames(p.steps.length, enterOpts) : enters;
  const progress = k.kit ? stepProgress(f, enters, k.fps) : { fill: 0, opacity: 0, active: false };
  const horizontal = s.aspect === '16:9';
  // 📐 ★세로 화면은 단계 카드를 «한 줄형»(번호 왼쪽 · 글 오른쪽)으로 눕힌다★ — 번호 원 위에 글을 쌓는
  //   종전 카드는 한 장이 약 170px 라, 5단계면 화살표까지 약 1,370px 로 세이프존 세로 폭(1,000px @1920)을
  //   넘었다(렌더 실측: 글자의 64% 가 안전 구역 밖 · 종전 여백에서도 60%). 한 줄형은 한 장이 약 114px 라
  //   5단계가 약 930px 에 든다. 화살표도 한 단 작게(`T.label` · 줄높이 1) — 세로 줄에서는 이음표일 뿐이다.
  const stacked = !horizontal;
  const mk = useStageMarks();
  // 🧊 무대 칸 계획 — 라벨이 한 줄로 드는 글자 크기·한 줄 칸 수. 한 줄에 다 안 들어가면 칸을 여러 줄로 쌓는다(무대 밖 undefined = 종전).
  const cells = useStageCells();
  const tight = useStageCompact() ? BRIEF_COMPACT_CELL : null; // 🧊 촘촘히(무대 안 · 보통 간격으로 안 들어갈 때만)
  const labelPx = s.px(cells ? cells.labelPx : T.caption);
  const multiRow = !!cells && horizontal && cells.perRow < p.steps.length;
  const rows: number[][] = [];
  if (multiRow) for (let i = 0; i < p.steps.length; i += cells!.perRow) rows.push(p.steps.slice(i, i + cells!.perRow).map((_, j) => i + j));
  const renderStep = (step: string, i: number) => {
    const stepEnterAt = enters[i];
    const anim = slideIn(f, { enterAt: stepEnterAt, duration: 18, direction: horizontal ? 'right' : 'up', ...k });
    // 화살표는 띄우지 않는다 — 단계 카드 사이를 «잇는» 기호라, 따로 떠다니면 연결이 끊겨 보인다.
    const stepFloat = sustainedFloat(f, i + 1, floatEnters[i] + motionEnterFrames(k, 18), s.px(FLOAT_AMP.panel));
    const op = cellOpacity(!!cells, anim.opacity);
    const inner = op.inner < 1 ? { opacity: op.inner } : {};
    return (
      <React.Fragment key={i}>
        {i > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent, fontSize: s.px(stacked ? T.label : T.body), lineHeight: stacked ? 1 : undefined, fontWeight: FW.bold, opacity: op.outer }}>
            {horizontal ? '→' : '↓'}
          </div>
        ) : null}
        <div
          {...mk.cell}
          style={{
            ...(CARD(p.cardStyle ?? 'outlined', C) as object), ...anim, opacity: op.outer,
            ...(tight ? { padding: `${tight.stepPadY}px 28px` } : {}),
            transform: `${anim.transform} translateY(${stepFloat}px)`,
            flex: multiRow ? `0 0 calc((100% - ${(cells!.perRow - 1) * s.px(T.body * 0.9 + 40)}px) / ${cells!.perRow})` : horizontal ? '1 1 0' : undefined,
            display: 'flex', flexDirection: stacked ? 'row' : 'column', alignItems: 'center',
            justifyContent: stacked ? 'flex-start' : 'center', gap: s.px(stacked ? 20 : tight ? tight.stepGap : 12),
          }}
        >
          <div
            style={{
              width: s.px(tight ? tight.stepCircle : 56), height: s.px(tight ? tight.stepCircle : 56), borderRadius: '50%', flexShrink: 0,
              border: `${Math.max(2, s.px(3))}px solid ${C.accent}`, color: C.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: s.px(T.label), fontWeight: FW.bold,
              // 🧷 kit — 번호 원만 소품 스프링으로 착지(단계 글자는 튀지 않는다).
              ...propPopStyle(k, f, stepEnterAt), ...inner,
            }}
          >
            {i + 1}
          </div>
          <div {...mk.label} style={{ ...inner, fontSize: labelPx, fontWeight: FW.medium, lineHeight: tight ? tight.stepLine : LINE_HEIGHT.normal, color: C.textMain, textAlign: stacked ? 'left' : 'center', ...KO_TEXT }}>
            <KitWords kit={k.kit} fps={k.fps} f={f} at={stepEnterAt} text={step}>
              <KeywordText text={step} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, step, stepEnterAt, f - stepEnterAt - 10)} />
            </KitWords>
          </div>
        </div>
      </React.Fragment>
    );
  };
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      {p.title && !hideTitle ? (
        <div ref={titleRef} style={{ ...kitHold(titleAnim, k),
          transform: `${titleAnim.transform} translateY(${sustainedFloat(f, 0, titleSettled, s.px(FLOAT_AMP.text))}px) scale(${push})`,
          fontSize: s.px(T.header), fontWeight: FW.semibold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, marginBottom: s.px(stacked ? 36 : 52), ...KO_TEXT }}>
          <KitWords kit={k.kit} fps={k.fps} f={f} at={4} text={title}>
            <KeywordText text={title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, title, 4, f - 20)} />
          </KitWords>
        </div>
      ) : null}
      {multiRow ? (
        /* 🧊 여러 줄 칸(무대 안 · 라벨이 한 줄에 다 안 들어갈 때만) — 줄마다 같은 칸 폭 · 진행 막대는 쓰지 않는다(줄을 건너는 막대는 칸과 길이가 안 맞는다). */
        <div style={{ display: 'flex', flexDirection: 'column', gap: s.px(tight ? tight.stepRowGap : 20), width: '100%', transform: `scale(${push})` }}>
          {rows.map((idxs, r) => (
            <div key={r} style={{ display: 'flex', flexDirection: 'row', gap: s.px(20), alignItems: 'stretch', justifyContent: 'center', width: '100%' }}>
              {idxs.map((i) => renderStep(p.steps[i], i))}
            </div>
          ))}
        </div>
      ) : (
      <div style={{ display: 'flex', flexDirection: horizontal ? 'row' : 'column', gap: s.px(stacked ? 8 : 20), alignItems: 'stretch', justifyContent: 'center', width: '100%', transform: `scale(${push})`,
        // ⑤ 진행 막대가 떠 있는 동안만 기준 상자가 된다(오프셋 없는 relative = 배치 불변 · 끝나면 키째 빠진다).
        ...(progress.active ? { position: 'relative' as const } : {}) }}>
        {progress.active ? (
          /* ⑤ 진행 막대 — 단계가 들어올 때마다 채워지고 마지막 단계 뒤 사라진다(남기지 않는다 · 번호·도장 없음). */
          <div style={{ position: 'absolute', background: C.accent, opacity: progress.opacity, borderRadius: s.px(3),
            ...(horizontal
              ? { left: 0, top: -s.px(stacked ? 12 : 22), height: s.px(6), width: `${(progress.fill * 100).toFixed(3)}%` }
              : { top: 0, left: -s.px(18), width: s.px(6), height: `${(progress.fill * 100).toFixed(3)}%` }) }} />
        ) : null}
        {p.steps.map((step, i) => renderStep(step, i))}
      </div>
      )}
    </Frame>
  );
};

// ─── 8. ComparePair — 잘못 vs 올바름 (2) ───────────────────────────

export interface CompareSide { title: string; body?: string[] }

export const ComparePair: React.FC<LayoutBaseProps & { title?: string; bad: CompareSide; good: CompareSide }> = (p) => {
  const { colors: C, f, revealMode = 'sequential', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const hideTitle = useStageHidesTitle(); // 🧊 모션 브리핑 무대가 제목을 도형 위에 따로 띄우면 여기선 그리지 않는다
  const [titleRef, title] = useBalancedTitle(p.title, { avail: 'parent', keyword: p.keyword }); // 📐 #6394 마지막 줄 한 어절 막기(실측 · 고른 제목은 그대로)
  const k = kitOptOf(p, s.fps);
  const titleAnim = slideIn(f, { enterAt: 4, duration: 20, ...k });
  const push = sustainedPushIn(f);  // 🎞 지속 모션 — 규율은 파일 상단 절 참조
  const titleSettledAt = 4 + motionEnterFrames(k, 20); // enterAt 4 + duration 20(종전) · kit 이면 토큰 길이
  const horizontal = s.aspect === '16:9';
  const mk = useStageMarks();
  // 🧹 화면이 ✕·✓ 를 붙이므로 제목 앞머리의 같은 뜻 기호(❌·✅…)는 그릴 때 뗀다(«✕❌ 잘못된 대응» 방지).
  const sides = [
    { side: { ...p.bad, title: stripLeadingMark(p.bad.title, 'cross') }, mark: '✕', color: C.status?.dangerText ?? C.accent, border: C.status?.danger ?? C.accent },
    { side: { ...p.good, title: stripLeadingMark(p.good.title, 'check') }, mark: '✓', color: C.status?.successText ?? C.accent, border: C.status?.success ?? C.accent },
  ];
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      {p.title && !hideTitle ? (
        <div ref={titleRef} style={{ ...kitHold(titleAnim, k),
          transform: `${titleAnim.transform} translateY(${sustainedFloat(f, 0, titleSettledAt, s.px(FLOAT_AMP.text))}px) scale(${push})`,
          fontSize: s.px(T.header), fontWeight: FW.semibold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, marginBottom: s.px(48), ...KO_TEXT }}>
          <KitWords kit={k.kit} fps={k.fps} f={f} at={4} text={title}>
            <KeywordText text={title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, title, 4, f - 20)} />
          </KitWords>
        </div>
      ) : null}
      <div style={{ display: 'flex', flexDirection: horizontal ? 'row' : 'column', gap: s.px(40), alignItems: 'stretch', justifyContent: 'center', width: '100%', maxWidth: s.px(1600), transform: `scale(${push})` }}>
        {sides.map(({ side, mark, color, border }, i) => {
          const cardEnterAt = enterAtFor(i, revealMode, p.title ? 18 : 6);
          const anim = slideIn(f, { enterAt: cardEnterAt, duration: 20, ...k });
          // 위상을 달리한다 — 같이 흔들리면 두 패널이 «한 덩어리»로 보여 비교가 흐려진다.
          const float = sustainedFloat(f, i + 1, cardEnterAt + motionEnterFrames(k, 20), s.px(FLOAT_AMP.panel));
          return (
            <div
              key={i}
              {...mk.cell}
              style={{
                ...(CARD(p.cardStyle ?? 'filled', C) as object), ...anim,
                transform: `${anim.transform} translateY(${float}px)`,
                borderTop: `${Math.max(3, s.px(4))}px solid ${border}`,
                flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: s.px(16), textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: s.px(16) }}>
                <span style={{ color, fontSize: s.px(T.header), fontWeight: FW.bold, lineHeight: LINE_HEIGHT.tight }}><KitPop kit={k.kit} fps={k.fps} f={f} at={clickAt(k, cardEnterAt)}>{mark}</KitPop></span>
                <span {...mk.label} style={{ fontSize: s.px(T.body), fontWeight: FW.bold, lineHeight: LINE_HEIGHT.tight, color: C.textMain, ...KO_TEXT }}>
                  <KitWords kit={k.kit} fps={k.fps} f={f} at={cardEnterAt} text={side.title}>
                    <KeywordText text={side.title} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, side.title, cardEnterAt, f - enterAtFor(i, revealMode, 18) - 12)} />
                  </KitWords>
                </span>
              </div>
              {(side.body ?? []).map((line, j) => (
                <div key={j} style={{ fontSize: s.px(T.caption), fontWeight: FW.regular, lineHeight: LINE_HEIGHT.loose, color: C.textSub, ...KO_TEXT }}>
                  <KitWords kit={k.kit} fps={k.fps} f={f} at={cardEnterAt + kitFrames(150 * (j + 1), k.fps)} text={line}>{line}</KitWords>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

// ─── 9. MediaFocus — 미디어 + 캡션 (VM 전용, 1+1) ──────────────────

export const MediaFocus: React.FC<LayoutBaseProps & { src: string; caption: string }> = (p) => {
  const { colors: C, f, revealMode = 'all', anchor = 'center' } = p;
  const s = useSafeArea(p.aspect);
  const k = kitOptOf(p, s.fps);
  const mediaEnter = enterAtFor(0, revealMode);
  const capEnter = enterAtFor(1, revealMode, 16);
  const m = slideIn(f, { enterAt: mediaEnter, duration: 22, ...k });
  const c = slideIn(f, { enterAt: capEnter, duration: 20, ...k });
  // 여기만 밀기 대상이 «액자»가 아니라 «사진»이다 — 액자를 키우면 둥근 모서리·그림자까지 커져
  // 레이아웃이 밀린다. 안쪽 사진만 밀면 액자는 제자리에 있고 그림만 서서히 다가온다(켄 번즈).
  const push = sustainedPushIn(f);
  // 📐 ★사진 높이에 상한을 둔다★ — 폭 100% + 자연 높이였던 종전 액자는 **세로 사진**(9:16 씬 그림이 기본)에서
  //   높이가 폭의 1.78배가 되어 안전 구역을 뚫고, 아래 설명 글을 화면 밖(설명글·버튼 자리)으로 밀어냈다
  //   (렌더 실측: 설명 글 100% 가 안전 구역 밖). 상한 = 안전 구역 높이 − 설명 두 줄 몫. 넘치는 사진은
  //   가운데를 기준으로 잘라 채운다(`objectFit: cover` · 제품이 보통 가운데에 있다). 가로 사진은 상한에
  //   닿지 않아 종전과 같다.
  const mediaMaxH = Math.max(0, s.H - s.safeT - s.safeB - s.px(32) - Math.round(s.px(T.body) * LINE_HEIGHT.normal * 2));
  return (
    <Frame anchor={anchor} aspect={p.aspect}>
      <div style={{ ...m,
        transform: `${m.transform} translateY(${sustainedFloat(f, 0, mediaEnter + motionEnterFrames(k, 22), s.px(FLOAT_AMP.panel))}px)`,
        width: '100%', maxWidth: s.px(1280), borderRadius: s.px(16), overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.40)' }}>
        <SceneImg src={p.src} style={{ width: '100%', maxHeight: mediaMaxH, objectFit: 'cover', display: 'block', transform: `scale(${push})` }} />
      </div>
      <div style={{ ...kitHold(c, k),
        transform: `${c.transform} translateY(${sustainedFloat(f, 1, capEnter + motionEnterFrames(k, 20), s.px(FLOAT_AMP.text))}px)`,
        marginTop: s.px(32), fontSize: s.px(T.body), fontWeight: FW.medium, lineHeight: LINE_HEIGHT.normal, color: C.textMain, ...KO_TEXT }}>
        <KitWords kit={k.kit} fps={k.fps} f={f} at={capEnter} text={p.caption}>
          <KeywordText text={p.caption} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, p.caption, capEnter, f - enterAtFor(1, revealMode, 16) - 12)} />
        </KitWords>
      </div>
    </Frame>
  );
};

// ─── 10. PosterStack — 이미지가 화면을 채우고 그 «위»에 글자가 층층이 쌓인다 ──────
//
// ★왜 `mediaFocus` 와 별개인가★ — 저쪽은 **액자 안 이미지 + 그 아래 캡션 한 줄**이라 배치가 반대다.
//   포스터는 그림이 배경이고 글자가 그 위에 얹히며, 층이 **여럿**이고 **차례로** 쌓여 한 장이 된다.
//   (근거 = 벤치 v015 실측: 0초 제목 · 0.6초 딱지와 줄 · 2.6초 한마디 · 그 뒤 정지가 «읽는 시간».)
// ★새로 만든 것은 배치뿐이다★ — 등장(`slideIn` 기본 방향 `up` = 아래에서 솟음) · 순차 시각
//   (`itemEnterFrames` — 씬 길이에 고르게 펴고 문장 경계에 맞춘다) · 지속 모션(`sustainedPushIn`
//   켄번즈 · `sustainedFloat`) · 강조(`KeywordText`) · 안전 영역은 전부 기존 부품을 그대로 쓴다.
// ★글자 가독성★ — 사진 위 글자라 위·아래에 어둠 깔개를 깐다(하단 자막 띠와 같은 관용구).
//   깔개가 없으면 밝은 제품컷에서 흰 글자가 사라진다.
export const PosterStack: React.FC<
  LayoutBaseProps & { src: string; headline: string; badge?: string; compare?: string; punch?: string }
> = (p) => {
  const { colors: C, f, revealMode = 'sequential' } = p;
  const s = useSafeArea(p.aspect);
  const k = kitOptOf(p, s.fps);
  // 층 순서 = 화면에 쌓이는 순서. 빈 층은 자리를 차지하지 않는다(등장 시각도 당겨진다).
  // ★글자가 이 형식의 절반이다★ — 포스터는 «읽히는 것»이 목적이라 본문 토큰(56)으로는 밋밋하다.
  //   벤치 실측에서도 제목 띠와 견주는 줄이 화면 폭을 거의 채운다. 그래서 제목·견줌은 hero 급,
  //   딱지·한마디는 그보다 한 단 아래로 둔다(네 층이 같은 크기면 무엇이 중요한지가 사라진다).
  //   ⚠️ 크기는 **안전 폭에 맞춰 잡는다**(2026-09-23) — 세로 안전 폭은 화면의 79.7% 뿐이라
  //      hero(132) 를 쓰면 제목이 세 줄로 접혀 그림을 덮는다(실측). title(108) 이 두 줄 안에 든다.
  const layers = [
    { key: 'headline', text: p.headline, size: T.title, weight: FW.bold, top: true },
    { key: 'badge', text: p.badge, size: T.body, weight: FW.semibold, top: false },
    { key: 'compare', text: p.compare, size: T.title, weight: FW.bold, top: false },
    { key: 'punch', text: p.punch, size: T.body, weight: FW.medium, top: false },
  ].filter((l) => (l.text ?? '').trim() !== '');
  const layerOpts = { beats: p.beats, sceneFrames: p.sceneFrames, revealMode, base: 6 };
  const at = itemEnterFrames(layers.length, { ...layerOpts,
    holdFrames: holdFramesOf(k, layers.length ? String(layers[layers.length - 1].text) : undefined),
  });
  const floatAt = k.kit ? itemEnterFrames(layers.length, layerOpts) : at; // 부유 위상 = 종전 등장 시각 기준
  const push = sustainedPushIn(f);

  const layerNode = (l: (typeof layers)[number], i: number) => {
    const a = slideIn(f, { enterAt: at[i], duration: 20, ...k });
    return (
      <div
        key={l.key}
        style={{ ...kitHold(a, k),
          transform: `${a.transform} translateY(${sustainedFloat(f, i, floatAt[i] + motionEnterFrames(k, 20), s.px(FLOAT_AMP.text))}px)`,
          width: '100%', textAlign: 'center',
          fontSize: s.px(l.size), fontWeight: l.weight, lineHeight: LINE_HEIGHT.tight,
          color: C.textMain, textShadow: `0 ${s.px(2)}px ${s.px(12)}px rgba(0,0,0,0.75)`, ...KO_TEXT }}
      >
        <KitWords kit={k.kit} fps={k.fps} f={f} at={at[i]} text={l.text as string}>
          <KeywordText text={l.text as string} keyword={p.keyword} emphasis={p.keywordEmphasis} colors={C} f={kwF(k, f, l.text as string, at[i], f - at[i] - 12)} />
        </KitWords>
      </div>
    );
  };

  const top = layers.filter((l) => l.top);
  const bottom = layers.filter((l) => !l.top);
  return (
    <AbsoluteFill>
      {/* 배경 = 제품 그림. 화면을 꽉 채우고 아주 느리게 다가간다(켄 번즈). */}
      <AbsoluteFill style={{ overflow: 'hidden' }}>
        <SceneImg
          src={p.src}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: `scale(${push})` }}
        />
      </AbsoluteFill>
      {/* 어둠 깔개 — 위·아래만. 가운데 제품은 가리지 않는다.
          ★밝은 제품컷에서 흰 글자가 사라지지 않을 만큼만★ 짙게 한다(가운데는 0 이라 그림이 안 죽는다). */}
      <AbsoluteFill style={{ pointerEvents: 'none',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.22) 26%, rgba(0,0,0,0) 46%, rgba(0,0,0,0.30) 64%, rgba(0,0,0,0.78) 100%)' }} />
      {/* 📐 글자 층은 세이프존 안(`useSafeArea` = 실측 정본 core · 좌우 비대칭). 그림은 위 배경층이라 화면 끝까지 간다 —
          그림은 가장자리가 UI 에 덮여도 뜻이 살고, 글자는 덮이면 뜻이 죽는다. */}
      <AbsoluteFill style={{
        paddingTop: s.safeT, paddingBottom: s.safeB, paddingLeft: s.safeL, paddingRight: s.safeR,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: s.px(10) }}>
          {top.map((l) => layerNode(l, layers.indexOf(l)))}
        </div>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: s.px(14) }}>
          {bottom.map((l) => layerNode(l, layers.indexOf(l)))}
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── 디스패치 — MSB layout.type → 컴포넌트 (결정론 조립 진입점) ──────
// cardGrid 는 content.cols 로 2/3 분기. custom 은 여기서 처리하지 않음 (escape hatch — P2에서 LLM codegen).

export const LAYOUT_COMPONENTS = {
  fullText: FullText,
  cardGrid: CardGrid,
  checkList: CheckList,
  statRow: StatRow,
  bigNumber: BigNumber,
  splitFocus: SplitFocus,
  stepFlow: StepFlow,
  comparePair: ComparePair,
  mediaFocus: MediaFocus,
  posterStack: PosterStack,
} as const;

export type LayoutComponentId = keyof typeof LAYOUT_COMPONENTS;
